class ServicesAndDeliveryUtils {
    RELATES_TO_PRODUCT_TITLE_LENGTH = 60;

    constructor() {
        if (ServicesAndDeliveryUtils._instance) {
            return ServicesAndDeliveryUtils._instance;
        }

        this._addEventListeners();

        this.cachedShippingPrice = {};
        this._shippingPricePromises = {};

        ServicesAndDeliveryUtils._instance = this;
    }

    /**-------- Common functionality start -------*/

    /**
     * Requets cart via http
     */
    /**
     * Fetches the cart, ensures multiple simultaneous calls use the same request,
     * and caches the result for 1 second.
     */
    async requestCart() {
        if (!this._cartCache) {
            this._cartCache = {
                promise: null,
                result: null,
                timestamp: 0
            };
        }

        const CACHE_DURATION = 1;
        const now = Date.now();

        // Return cached result if still valid
        if (
            this._cartCache.result !== null &&
            (now - this._cartCache.timestamp) < CACHE_DURATION
        ) {
            return this._cartCache.result;
        }

        // If a fetch is in progress, return its promise
        if (this._cartCache.promise) {
            return this._cartCache.promise;
        }

        // Make a new fetch and remember the promise
        this._cartCache.promise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(res => {
                if (!res.ok) {
                    console.error('Cannot request cart info');
                    throw new Error('Cannot request cart info');
                }
                return res.json();
            })
            .then(data => {
                this._cartCache.result = data;
                this._cartCache.timestamp = Date.now();
                this._cartCache.promise = null;
                return data;
            })
            .catch(e => {
                this._cartCache.promise = null;
                this._cartCache.result = null;
                this._cartCache.timestamp = 0;
                return undefined;
            });

        return this._cartCache.promise;
    }

    /**-------- Common functionality end -------*/


    /**------- Shipping prices functionality start ------*/

    /**
     * Get shipping price with caching mechanism
     */
    async getShippingPrice() {
        const cart = await this.requestCart();

        const apiPayload = this._getCourierApiPayload(cart);
        const apiPayloadString = btoa(encodeURIComponent(JSON.stringify(apiPayload)));

        let shippingPrice = this.cachedShippingPrice[apiPayloadString] || null;

        if (shippingPrice) {
            return shippingPrice;
        }

        if (this._shippingPricePromises[apiPayloadString]) {
            return await this._shippingPricePromises[apiPayloadString];
        }

        const shippingPricePromise = this._requestShippingPrice(apiPayload)
            .then(result => {
                this.cachedShippingPrice[apiPayloadString] = result;
                delete this._shippingPricePromises[apiPayloadString];
                return result;
            })
            .catch(error => {
                delete this._shippingPricePromises[apiPayloadString];
                throw error;
            });

        this._shippingPricePromises[apiPayloadString] = shippingPricePromise;

        return await shippingPricePromise;
    }

    /**
    * Requests shipping price via http
    */
    async _requestShippingPrice(courierApiPayload) {
        let result = [];

        if (!window.serviceAndDeliveryUtils.config.shopifyDomain) {
            return;
        }

        try {
            const res = await fetch(window.serviceAndDeliveryUtils.config.shippingCarrierApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Shop-Domain': window.serviceAndDeliveryUtils.config.shopifyDomain
                },
                body: JSON.stringify(courierApiPayload),
            });

            if (!res.ok) {
                throw new Error(`Request failed: ${res.status} ${res.statusText}`);
            }

            result = await res.json();
        } catch (error) {
            console.error('Error making shipping price request:', error);
        }

        return result;
    }

    _getCourierApiPayload(cart) {
        const items = cart.items
            .filter(item => !item.properties || item.properties['_is_service_product'] !== true)
            .map(item => {
                return {
                    item_id: item.id,
                    product_id: item.product_id,
                    quantity: item.quantity,
                    price: item.final_line_price
                }
            });

        const currency = cart.currency;
        const locale = window.serviceAndDeliveryUtils.config.locale

        return {
            "rate": {
                "items": items,
                "currency": currency,
                "locale": locale
            }
        };
    }

    /**-------- Shipping prices functionality end -------*/


    /**-------- Additional services functionality start -------*/

    _addEventListeners() {
        // Adds a global event listener to trigger syncServices when the event is dispatched.
        // Optionally pass a cart via event.detail.cart, otherwise it will request it from API.
        // Can be triggered using: 
        //   window.dispatchEvent(new CustomEvent('servicesAndDeliveryUtils:cartUpdated', { detail: { cart /* optional */ } }));
        window.addEventListener('servicesAndDeliveryUtils:cartUpdated', async (event) => {
            let cart = event?.detail?.cart;
            if (!cart || !Array.isArray(cart.items)) {
                cart = await this.requestCart();
            }
            if (cart && Array.isArray(cart.items)) {
                await this._syncServices(cart);
            }
        });


        //when user quickly change quantities there may be difference between qty of non service and service products
        document.addEventListener('DOMContentLoaded', () => {
            if (this._isCartPage()) {
                window.dispatchEvent(new CustomEvent('servicesAndDeliveryUtils:cartUpdated', { detail: {} }));
            }
        });
    }

    _isCartPage() {
        return /(^|\/)cart\/?$/.test(window.location.pathname);
    }

    /**
     * This function check all cart items, and add/delete/change_quantities of additional service
     * products if addition service is selected for given product
     * 
     * Should be called on each iteraction with cart
     */
    async _syncServices(cart) {
        try {
            const sections = this._getCartRelatedSections();
            let currentCart = cart;

            let mappedServices = this._mapNonServiceCartItemsToServiceCartItems(currentCart.items);

            if (!this._isCartServicesInSync(mappedServices)) {
                const updatedCart = await this._adjustQuantitiesForExistingInCartServiceProducts(mappedServices, sections, false);
                if (updatedCart && Array.isArray(updatedCart.items)) {
                    currentCart = updatedCart;
                    mappedServices = this._mapNonServiceCartItemsToServiceCartItems(currentCart.items);
                }

                const additionCart = await this._addNewServiceProducts(mappedServices, sections, false);
                if (additionCart && Array.isArray(additionCart.items)) {
                    currentCart = additionCart;
                }
            }

            if (currentCart && currentCart.sections) {
                this._updateCartState(currentCart);
            }
        } catch (err) {
            console.error('Error in syncCartProductsAdditionalServidces', err);
        } finally {
            this.setCheckoutBlocked(false);
        }
    }

    _isCartServicesInSync(mappedServices) {
        let result = true;

        for (const relatedItems of Object.values(mappedServices)) {
            const { nonServiceCartItems, serviceCartItem } = relatedItems;

            const nonServiceCartItemsQty = nonServiceCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
            const serviceCartItemQty = serviceCartItem ? serviceCartItem.quantity : 0;

            if (nonServiceCartItemsQty !== serviceCartItemQty) {
                result = false;
                break;
            }

            if (nonServiceCartItemsQty > 0 && serviceCartItemQty > 0) {
                const expectedVariantId = this._getNonServiceCartItemServiceProductVariantId(nonServiceCartItems[0]);
                if (String(serviceCartItem.variant_id) !== String(expectedVariantId)) {
                    result = false;
                    break;
                }
            }
        }
       
        return result;
    }

    _getNonServiceCartItemServiceProductVariantId(nonServiceItem) {
        if (!('properties' in nonServiceItem)) {
            console.log(nonServiceItem);
            return null;
        }

        if (this._normalizeBoolean(nonServiceItem.properties['_is_lifting_service'])) {
            return window.serviceAndDeliveryUtils.config.liftingServiceProductVariantId;
        }

        if (this._normalizeBoolean(nonServiceItem.properties['_is_lifting_and_installation_service'])) {
            return window.serviceAndDeliveryUtils.config.liftingInstallationServiceProductVariantId;
        }

        return null;
    }

    /**
    * Adds new service products if necessary
    */
    async _addNewServiceProducts(mappedServices, sections, shouldUpdateState = true) {
        let cart = null;
        const itemsToAdd = [];

        for (const [serviceRelationId, relatedItems] of Object.entries(mappedServices)) {
            const { nonServiceCartItems, serviceCartItem } = relatedItems;

            const nonServiceCartItemsQty = nonServiceCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
            const serviceCartItemQty = serviceCartItem ? serviceCartItem.quantity : 0;

            if (nonServiceCartItemsQty && !serviceCartItemQty) {
                const nonServiceCartItem = nonServiceCartItems[0]; //we can take first as similar are grouped

                const serviceVariantId = this._getNonServiceCartItemServiceProductVariantId(nonServiceCartItems[0]);

                if (!serviceVariantId) {
                    continue;
                }
                
                const properties = {
                    _service_relation_id: String(serviceRelationId),
                    _is_service_product: true,
                    _is_lifting_service: this._normalizeBoolean(nonServiceCartItem.properties['_is_lifting_service']),
                    _is_lifting_and_installation_service: this._normalizeBoolean(nonServiceCartItem.properties['_is_lifting_and_installation_service'])
                }
                let relatesToTitle = nonServiceCartItem.title?.slice(0, this.RELATES_TO_PRODUCT_TITLE_LENGTH);
                if (nonServiceCartItem.title && nonServiceCartItem.title.length > this.RELATES_TO_PRODUCT_TITLE_LENGTH) {
                    relatesToTitle = relatesToTitle.trim() + '...';
                }
                properties[window.serviceAndDeliveryUtils.config.relatesToTitle] = relatesToTitle;
                properties['_relates_to'] = nonServiceCartItem.title || '';

                itemsToAdd.push({
                    id: serviceVariantId,
                    quantity: nonServiceCartItemsQty,
                    properties: properties
                });
            }
        }

        if (itemsToAdd.length > 0) {
            const body = JSON.stringify({
                items: itemsToAdd,
                sections: sections
            });

            await fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
                .then((response) => response.json())
                .then(async (parsedState) => {
                    if (parsedState.status) {
                        console.log('Error in status of _addNewServiceProducts', parsedState);
                        return;
                    }

                    cart = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
                    cart['sections'] = parsedState['sections'];

                    if (shouldUpdateState) {
                        this._updateCartState(cart);
                    }
                    this._dispatchProductAddedEvent(parsedState);
                })
                .catch((error) => {
                    console.log('Error in _addNewServiceProducts', error);
                });
        }

        return cart;
    }

    /**
     * Adjusts quantities and deletes unnecessary service products
     * 
     * returns updated cart if update happened or null if not or error occurred
     */
    async _adjustQuantitiesForExistingInCartServiceProducts(mappedServices, sections, shouldPublish = true) {
        let cart = null;
        const updates = {};

        for (const relatedItems of Object.values(mappedServices)) {
            const { nonServiceCartItems, serviceCartItem } = relatedItems;
            if (!serviceCartItem) continue;

            const nonServiceCartItemsQty = nonServiceCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
            const serviceCartItemQty = serviceCartItem ? serviceCartItem.quantity : 0;

            const expectedVariantId = nonServiceCartItems.length > 0 ? this._getNonServiceCartItemServiceProductVariantId(nonServiceCartItems[0]) : null;
            const isVariantWrong = nonServiceCartItemsQty && serviceCartItemQty && String(serviceCartItem.variant_id) !== String(expectedVariantId);

            const isQuantityOfServiceProductDiffers = nonServiceCartItemsQty && serviceCartItemQty && nonServiceCartItemsQty !== serviceCartItemQty;
            const isRelatedNonServiceProductWasDeleted = serviceCartItemQty && !nonServiceCartItemsQty;

            if (isQuantityOfServiceProductDiffers || isRelatedNonServiceProductWasDeleted || isVariantWrong) {
                let quantity = isVariantWrong ? 0 : (isQuantityOfServiceProductDiffers ? nonServiceCartItemsQty : 0);
                updates[serviceCartItem.key] = quantity;
            }
        }

        if (Object.keys(updates).length > 0) {
            const body = JSON.stringify({
                updates: updates,
                sections: sections
            });

            await fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
                .then((response) => response.json())
                .then(async (cartResponse) => {
                    if (cartResponse.status) {
                        console.log('Error in status of _adjustQuantitiesForExistingInCartServiceProducts', cartResponse);
                        return;
                    }

                    cart = cartResponse;
                    if (shouldPublish) {
                        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                            cart: cart,
                        });
                    }
                })
                .catch((error) => {
                    console.log('Error in _adjustQuantitiesForExistingInCartServiceProducts', error);
                });
        }

        return cart;
    }

    _updateCartState(cartJson) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    };

    _dispatchProductAddedEvent(parsedState) {
        document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
                detail: {
                    product: parsedState,
                },
            })
        );
    };

    _getCartRelatedSections() {
        let sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        return sections;
    }


    /**
     * returns 
     * [
     *      serviceRelationId => {
     *          nonServiceCartItems: [cartItem, ...], //several non service may be mapped to the same service
     *          serviceCartItem: cartItem|null
     *      }
     * ]
     * 
     */
    _mapNonServiceCartItemsToServiceCartItems(cartItems) {
        const result = [];
        if (!Array.isArray(cartItems)) return result;

        for (const item of cartItems) {
            const itemServiceRelationId = this._getCartItemServiceRelationId(item);
            if (!itemServiceRelationId && !this._isServiceCartItem(item)) {
                continue;
            }

            if (!result[itemServiceRelationId]) {
                result[itemServiceRelationId] = {
                    nonServiceCartItems: [],
                    serviceCartItem: null
                };
            }

            if (this._isServiceCartItem(item)) {
                result[itemServiceRelationId].serviceCartItem = item;
            } else {
                result[itemServiceRelationId].nonServiceCartItems.push(item);
            }
        }

        return result;
    }

    /**
     * Finds service mapped to item if it is exist
     */
    _findServiceCartItemForNonServiceCartItem(cartItems, nonServiceItem) {
        const nonServiceItemServiceRelationId = this._getCartItemServiceRelationId(nonServiceItem);

        let nonServiceItemExpectedServiceVariantId = null;
        if (this._normalizeBoolean(nonServiceItem.properties['_is_lifting_service'])) {
            nonServiceItemExpectedServiceVariantId = window.serviceAndDeliveryUtils.config.liftingServiceProductVariantId;
        } else if (this._normalizeBoolean(nonServiceItem.properties['_is_lifting_and_installation_service'])) {
            nonServiceItemExpectedServiceVariantId = window.serviceAndDeliveryUtils.config.liftingInstallationServiceProductVariantId;
        }

        if (nonServiceItemServiceRelationId === null || nonServiceItemExpectedServiceVariantId === null) {
            return null;
        }

        const foundService = cartItems.find(item => {
            const itemServiceRelationId = this._getCartItemServiceRelationId(item);

            return this._isServiceCartItem(item)
                && itemServiceRelationId !== null
                && itemServiceRelationId === nonServiceItemServiceRelationId
                && item.variant_id == nonServiceItemExpectedServiceVariantId
        })

        return foundService || null;
    }

    _isServiceCartItem(item) {
        const serviceProducts = [
            String(window.serviceAndDeliveryUtils.config.liftingServiceProductVariantId),
            String(window.serviceAndDeliveryUtils.config.liftingInstallationServiceProductVariantId)
        ];
        return serviceProducts.includes(String(item.variant_id));
    };

    _isNonServiceCartItemHasSelectedServices(item) {
        return ('properties' in item) &&
            (this._normalizeBoolean(item.properties['_is_lifting_service']) || this._normalizeBoolean(item.properties['_is_lifting_and_installation_service']));
    }

    _getCartItemServiceRelationId(cartItem) {
        const relationId = ('properties' in cartItem) ? cartItem.properties['_service_relation_id'] : null;

        return relationId ? String(relationId) : null;
    }

    /**-------- Additional services functionality end -------*/

    /**-------- Checkout blocking functionality start -------*/

    /**
     * Sets the checkout blocked state
     * @param {boolean} isBlocked - Whether checkout should be blocked
     */
    setCheckoutBlocked(isBlocked) {
        if (!this._isCartPage()) {
            return;
        }

        if (isBlocked) {
            document.body.classList.add('checkout-blocked');
        } else {
            setTimeout(() => {
                document.body.classList.remove('checkout-blocked');
            }, 1000);
        }
    }

    /**-------- Checkout blocking functionality end -------*/

    _normalizeBoolean(value) {
        return value === true || value === 'true' || value === '1' || value === 1;
    }
}

window.serviceAndDeliveryUtils.ServicesAndDeliveryUtilsService = new ServicesAndDeliveryUtils();